import { SafeArticleHtml } from '@/components/ui/SafeArticleHtml'
import { type ArticlePalette, parsePaletteElement } from '@/lib/article-palette'
import { ARTICLE_PROSE_CLASS } from '@/lib/article-typography'
import { PaletteMatrix } from './PaletteMatrix'

/**
 * An article's stored body, rendered read-only.
 *
 * The one read surface for article content — the Brand details tab uses it, and so
 * would any preview. It renders the same two things the composer does: prose through
 * the article sanitizer, and every palette block through `PaletteMatrix`.
 *
 * The palette goes through the component rather than through styled raw markup so
 * the reader and the author see the identical block, laid out identically, with the
 * hex code beneath each swatch instead of printed on top of an arbitrary colour.
 */

/** One run of the body: either prose HTML, or a palette block. */
type Segment = { kind: 'html'; html: string } | { kind: 'palette'; palettes: ArticlePalette[] }

/**
 * Split the body at each palette block, preserving order.
 *
 * Walks the top-level nodes only: a palette block is always a block-level child of
 * the document, and descending further would split a paragraph mid-sentence.
 */
export function splitArticleBody(html: string): Segment[] {
	const template = document.createElement('template')
	template.innerHTML = html

	const segments: Segment[] = []
	let buffer = ''

	const flush = () => {
		if (buffer.trim() !== '') segments.push({ kind: 'html', html: buffer })
		buffer = ''
	}

	for (const node of Array.from(template.content.childNodes)) {
		const element = node instanceof HTMLElement ? node : null
		if (element?.hasAttribute('data-article-palette')) {
			flush()
			segments.push({ kind: 'palette', palettes: parsePaletteElement(element) })
			continue
		}
		buffer += element ? element.outerHTML : (node.textContent ?? '')
	}
	flush()

	return segments
}

export function ArticleBody({ html, className }: { html: string; className?: string }) {
	const segments = splitArticleBody(html)

	return (
		<div className={className}>
			{segments.map((segment, index) =>
				segment.kind === 'palette' ? (
					<div key={index} className="my-6">
						<PaletteMatrix palettes={segment.palettes} />
					</div>
				) : (
					// Still sanitized: prose between palettes is author HTML like any other.
					<SafeArticleHtml key={index} html={segment.html} className={ARTICLE_PROSE_CLASS} />
				),
			)}
		</div>
	)
}
